import { add, eachDayOfInterval, formatDate, nextMonday } from 'date-fns';
import { de, enUS } from 'date-fns/locale';

import { type NumberSeparator } from '@vestigas/utils';

import { currentLocale } from './i18n';

export const date4server = 'yyyy-MM-dd';
export const dateLocalizedShort = 'd.MM.yy';
export const dateLocalizedShortWDay = 'EEEEEE d.MM.yy';
export const dateLocalizedShortest = 'd.M.yy';
export const dateLocalizedWMonthName = 'd. MMMM yyyy';
export const dateLocalizedWOYear = 'd. MMMM';
export const dateLocalizedWOYearShort = 'd.M.';
export const dateFull = 'd.M.yyyy';
export const dateFullDay = 'EEEE, d.M.yyyy';
export const dateLong = 'd.MM.yyyy';
export const dateMonthYear = 'M/yyyy';
export const dateMonthYearLong = 'MMMM yyyy';
export const dateMonthYearShort = 'M/yy';
export const datetimeLocalizedDefault = 'dd.MM.yyyy HH:mm';
export const datetime4server = "yyyy-MM-dd'T'HH:mm:ss'Z'";
export const datetime4datetimeInput = "yyyy-MM-dd'T'HH:mm";
export const time24h = 'H:mm';

// App-specific locale information (depends on i18n and date-fns)
// Uses validated getLocaleInfo which handles non-standard separators (e.g., NBSP, apostrophe)
const locale = currentLocale();
const LOCALE = {
  de,
  en: enUS,
};
const thousandsSeparator = Number(1000).toLocaleString(locale).charAt(1);
const decimalSeparator = Number(1.1).toLocaleString(locale).charAt(1);

const weekdays = eachDayOfInterval({
  start: nextMonday(new Date()),
  end: add(nextMonday(new Date()), { days: 6 }),
});

export const LOCALE_INFO = {
  decimal: decimalSeparator as NumberSeparator,
  name: locale,
  thousand: thousandsSeparator as NumberSeparator,
  weekdayNames: weekdays.map((day) =>
    formatDate(day, 'EEEE', { locale: LOCALE[locale] }),
  ),
  weekdayNamesShort: weekdays.map((day) =>
    formatDate(day, 'EEEEEE', { locale: LOCALE[locale] }),
  ),
};

/**
 * Regular expression for localized numbers.
 * Matches optional leading negative sign, initial sequence of one to three digits, optional groups of three digits separated by periods or commas (for thousand separators), and an optional decimal part with one or more digits.
 *
 * Examples:
 * 1234
 * -1234
 * 1,234
 * 1.234
 * 1,234.56
 * 1.234,56
 * 1234.5678
 * -1,234.5678
 */
export const localizedNumberRegex =
  '^-?[0-9]{1,3}(?:[.,][0-9]{3})*(?:[.,][0-9]+)?$';

const thousandsSeparatorRegex = new RegExp(`\\${LOCALE_INFO.thousand}`, 'g');
const decimalSeparatorRegex = new RegExp(`\\${LOCALE_INFO.decimal}`, '');

type SeparatorParts = {
  beforePart: string;
  afterPart: string;
};

type SeparatorType = 'decimal' | 'locale';

/**
 * Validates thousands grouping pattern (all groups except first must be 3 digits).
 * @param parts - Array of string parts split by separator
 * @returns true if valid thousands grouping, false otherwise
 */
function isValidThousandsGrouping(parts: string[]): boolean {
  for (let i = 1; i < parts.length; i++) {
    if (parts[i].length !== 3) {
      return false;
    }
  }

  return true;
}

/**
 * Handles normalization when only one type of separator exists (multiple occurrences).
 * @param normalized - The number string
 * @param separator - The separator character
 * @returns Normalized string or 'NaN' if invalid
 */
function handleMultipleSeparators(
  normalized: string,
  separator: string,
): string {
  const parts = normalized.split(separator);
  if (!isValidThousandsGrouping(parts)) {
    return 'NaN';
  }

  return normalized.replaceAll(separator, '');
}

/**
 * Handles normalization of a single comma.
 * @param normalized - The number string
 * @param lastCommaIndex - Index of the comma
 * @returns Normalized string or 'NaN' if invalid
 */
function handleSingleComma(normalized: string, lastCommaIndex: number): string {
  const { beforePart, afterPart } = extractSeparatedParts(
    normalized,
    lastCommaIndex,
  );

  const separatorType = determineSeparatorType(beforePart, afterPart);

  if (separatorType === 'decimal') {
    return normalized.replace(',', '.');
  }

  // Use locale-specific logic for ambiguous cases
  return normalized
    .replace(thousandsSeparatorRegex, '')
    .replace(decimalSeparatorRegex, '.');
}

/**
 * Handles normalization of a single dot.
 * @param normalized - The number string
 * @param lastDotIndex - Index of the dot
 * @returns Normalized string
 */
function handleSingleDot(normalized: string, lastDotIndex: number): string {
  const { beforePart, afterPart } = extractSeparatedParts(
    normalized,
    lastDotIndex,
  );

  const separatorType = determineSeparatorType(beforePart, afterPart);

  if (separatorType === 'locale') {
    // Use locale-specific logic for ambiguous cases
    return normalized
      .replace(thousandsSeparatorRegex, '')
      .replace(decimalSeparatorRegex, '.');
  }

  // Separator is decimal, dot is already in correct format
  return normalized;
}

/**
 * Extracts parts before and after a separator.
 *
 * @param normalized - The normalized number string (whitespace removed, ndash replaced, thousands separators removed, only decimal separator remains)
 * @param separatorIndex - Index of the separator character
 * @returns Object with beforePart and afterPart (excluding negative sign)
 */
function extractSeparatedParts(
  normalized: string,
  separatorIndex: number,
): SeparatorParts {
  const afterPart = normalized.slice(separatorIndex + 1);
  const beforePart = normalized.slice(
    normalized.startsWith('-') ? 1 : 0,
    separatorIndex,
  );

  return { beforePart, afterPart };
}

/**
 * Determines whether a separator is a decimal point or thousands separator.
 * Uses heuristics like digit counts and leading zeros to make intelligent decisions.
 *
 * @param beforePart - Substring before the separator
 * @param afterPart - Substring after the separator
 * @returns 'decimal' if decimal separator, 'locale' if ambiguous (uses locale logic)
 */
function determineSeparatorType(
  beforePart: string,
  afterPart: string,
): SeparatorType {
  const digitsBefore = beforePart.length;
  const digitsAfter = afterPart.length;

  // Use locale logic only for the ambiguous case:
  // 1-3 digits before (not just "0") and exactly 3 digits after
  // e.g. 1.000 or 100.001 -> could be thousands in German locale
  if (digitsAfter === 3 && digitsBefore <= 3 && beforePart !== '0') {
    return 'locale';
  }

  // All other cases: treat as decimal separator
  return 'decimal';
}

/**
 * Calculates the number of digits after the decimal point in a given number.
 *
 * @param {number} num - The input number to calculate the digits from.
 * @return {number} The number of digits after the decimal point.
 */
export const numberOfDigits = (number_: number) => {
  const digits = String(number_).split('.')[1] ?? '';
  return digits.length;
};

/**
 * Normalizes a string number by removing whitespace characters, replacing ndashes with a real minus character,
 * and replacing thousands separators with a dot.
 *
 * @param {string} number - The string number to be normalized.
 * @return {string} The normalized string number.
 */
export const normalizeStringNumber = (number: string) => {
  if (!number) {
    return number;
  }

  let normalized = number
    .replaceAll(/\s/g, '') // Remove all whitespace characters.
    .replace('–', '-'); // Replace ndashes and similar looking characters with a real minus character.

  // Handle leading decimal separator: .5 or ,5 → 0.5
  const hasNegative = normalized.startsWith('-');
  const withoutNegative = hasNegative ? normalized.slice(1) : normalized;
  if (withoutNegative.startsWith('.') || withoutNegative.startsWith(',')) {
    normalized = hasNegative ? '-0' + withoutNegative : '0' + withoutNegative;
  }

  // Detect invalid formats (consecutive separators, improper patterns)
  if (/[.,]{2,}/.test(normalized)) {
    // Consecutive separators like .. or ,, or ., are invalid
    return 'NaN';
  }

  const lastCommaIndex = normalized.lastIndexOf(',');
  const lastDotIndex = normalized.lastIndexOf('.');
  const commaCount = (normalized.match(/,/g) ?? []).length;
  const dotCount = (normalized.match(/\./g) ?? []).length;

  // If both separators exist, the rightmost one is the decimal separator
  if (lastCommaIndex > -1 && lastDotIndex > -1) {
    if (lastCommaIndex > lastDotIndex) {
      // Dot is thousands, comma is decimal: e.g. 1.000,50
      normalized = normalized.replaceAll('.', '').replace(',', '.');
    } else {
      // Comma is thousands, dot is decimal: e.g. 1,000.50
      normalized = normalized.replaceAll(',', '');
    }
  } else if (lastCommaIndex > -1) {
    // Only commas exist
    if (commaCount > 1) {
      normalized = handleMultipleSeparators(normalized, ',');
      if (normalized === 'NaN') {
        return 'NaN';
      }
    } else {
      normalized = handleSingleComma(normalized, lastCommaIndex);
    }
  } else if (lastDotIndex > -1) {
    // Only dots exist
    if (dotCount > 1) {
      normalized = handleMultipleSeparators(normalized, '.');
      if (normalized === 'NaN') {
        return 'NaN';
      }
    } else {
      normalized = handleSingleDot(normalized, lastDotIndex);
    }
  }

  return normalized;
};

/**
 * Parses a string or number into a number.
 *
 * @param {string | number} number - The input number to be parsed.
 * @return {number} The parsed number, or NaN if the input is not a valid number.
 */
export const parseNumber = (number?: string | number) => {
  if (typeof number === 'string') {
    const normalized = normalizeStringNumber(number);
    return Number.parseFloat(normalized);
  }

  return number ?? Number.NaN;
};

/**
 * Formats a number with a sign.
 *
 * @param {number} number - The number to be formatted.
 * @param {string} formatted - The formatted string representation of the number.
 * @param {boolean} showSign - Whether to show the sign of the number.
 * @param {boolean} [showAsNegative] - Whether to show the number as negative.
 * @return {string} The formatted string with a sign.
 */
export const formatSign = (
  number: number,
  formatted: string,
  showSign: boolean,
  showAsNegative?: boolean,
) => {
  if (number > 0 && showSign) {
    return `+ ${formatted}`;
  }

  if (number > 0 && showAsNegative) {
    return `- ${formatted}`;
  }

  return formatted.replace(/^-/, '- ');
};

/**
 * Formats a number into a string without using scientific notation.
 *
 * @param {number} value - The number to be formatted.
 * @return {string} The formatted number as a string.
 */
export const numberToFullWideString = (value: number) =>
  Number(value).toLocaleString([LOCALE_INFO.name, 'fullwide'], {
    maximumFractionDigits: 20, // Ensure that the number is not truncated.
    useGrouping: false, // Prevent grouping digits with a thousands separator.
  });

/**
 * Formats a number into a string with the specified number of digits and sign.
 *
 * @param {number | string} numberToFormat - The number or string to be formatted.
 * @param {number} [digits=0] - The number of digits to be displayed after the decimal point.
 * @param {boolean} [showSign=false] - Whether to show the sign of the number.
 * @param {boolean} [showAsNegative] - Whether to show the number as negative, even if it is positive.
 * @return {string} The formatted number as a string.
 */
export const formatNumber = (
  numberToFormat: number | string,
  digits = 0,
  showSign = false,
  showAsNegative?: boolean,
) => {
  if (numberToFormat === null) {
    return null;
  }

  const floatNumber =
    typeof numberToFormat === 'string'
      ? parseNumber(numberToFormat)
      : numberToFormat;
  const formatted = floatNumber.toLocaleString(LOCALE_INFO.name, {
    useGrouping: true,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  return formatSign(floatNumber, formatted, showSign, showAsNegative);
};
